import StorageService from "../services/StorageService";
import { getProfileRouteByRole } from "./roleSwitcher";

/**
 * Determines the profile route based on user role
 * @returns {string} Profile route path
 */
export function getProfileRoute() {
  const user = StorageService.getUser();
  
  if (!user) {
    return "/login";
  }

  // Try to use role switcher first
  try {
    const roleBasedRoute = getProfileRouteByRole();
    // Only return quality inspector route if user actually has Quality Inspector role
    if (roleBasedRoute) {
      if (roleBasedRoute === '/quality-inspector-profile') {
        // Verify user has Quality Inspector role based on user_type_id = 4
        let userTypeId = user.user_type_id;
        if (userTypeId === null || userTypeId === undefined) {
          userTypeId = '';
        } else {
          userTypeId = String(userTypeId).trim();
        }
        const typeIds = userTypeId 
          ? userTypeId.split(',').map(id => String(id).trim()).filter(id => id && id !== 'null' && id !== 'undefined')
          : [];
        
        // Quality Inspector has user_type_id = 4
        const hasQualityInspectorRole = typeIds.includes('4') || userTypeId === '4';
        
        if (hasQualityInspectorRole) {
          return roleBasedRoute;
        }
        // If route is quality inspector but user doesn't have the role, fall through to default logic
      } else {
        // For buyer/seller routes, return them directly
        return roleBasedRoute;
      }
    }
  } catch (e) {
    // Fallback to original logic if role switcher fails
  }

  // user_type_id is stored as comma-separated IDs (e.g., "1,2" or "2" or just 2 as number)
  let userTypeId = user.user_type_id;
  
  // Handle different formats: number, string, null, undefined
  if (userTypeId === null || userTypeId === undefined) {
    userTypeId = "";
  } else {
    userTypeId = String(userTypeId).trim();
  }
  
  // Convert to array of IDs
  const typeIds = userTypeId 
    ? userTypeId.split(",").map(id => String(id).trim()).filter(id => id && id !== "null" && id !== "undefined")
    : [];
  
  // Check user_type_id values
  // Mapping: 1 = BUYER, 2 = SELLER, 3 = BOTH
  // If user has multiple roles, prioritize SELLER
  
  // First, check if user_type_id is a single number (not comma-separated)
  const singleId = parseInt(userTypeId);
  if (!isNaN(singleId) && userTypeId.indexOf(",") === -1) {
    if (singleId === 2) {
      return "/seller-profile";
    }
    if (singleId === 1) {
      return "/buyer-profile";
    }
    if (singleId === 3) {
      return "/seller-profile";
    }
  }
  
  // Check comma-separated values
  if (typeIds.length > 0) {
    // Check for SELLER first (ID = 2) - highest priority
    const hasSeller = typeIds.some(id => {
      const numId = parseInt(id);
      return id === "2" || numId === 2;
    });
    if (hasSeller) {
      return "/seller-profile";
    }
    
    // Check for BUYER (ID = 1)
    const hasBuyer = typeIds.some(id => {
      const numId = parseInt(id);
      return id === "1" || numId === 1;
    });
    if (hasBuyer) {
      return "/buyer-profile";
    }
    
    // Check for BOTH (ID = 3) - if both, default to seller
    const hasBoth = typeIds.some(id => {
      const numId = parseInt(id);
      return id === "3" || numId === 3;
    });
    if (hasBoth) {
      return "/seller-profile";
    }
  }
  
  // Fallback - check other role fields if available
  const role = user.role || user.company_role || "";
  const roleUpper = String(role).toUpperCase().trim();
  
  if (roleUpper.includes("SELLER")) {
    return "/seller-profile";
  }
  
  if (roleUpper.includes("BUYER")) {
    return "/buyer-profile";
  }
  
  if (roleUpper.includes("BOTH")) {
    return "/seller-profile";
  }
  
  // Default to buyer profile
  return "/buyer-profile";
}

